
import { Component, Emit, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import { ref } from 'vue'
import BasicImage from '@mg/web-components/Atoms/Image/BasicImage'
import {
  HeaderDealsButton,
  HeaderLogo,
  MALL_ICONS,
  RoundedButton,
  useHeaderScroll,
} from '@mg/micro/vue2'
import { HeaderShell } from '@mg/micro/vue2/HeaderShell'
import { WebComponentsConfiguration } from '@mg/web-components/webComponentsConfigurationPlugin'
import HeaderMobileIcons from './HeaderMobileIcons.vue'
import HeaderDesktopIcons from './HeaderDesktopIcons.vue'
import HeaderMainMenu from '~/components/header/HeaderMainMenu.vue'
import HeaderHorizonMenu from '~/components/header/HeaderHorizonMenu.vue'
import HeaderMenuToggleButton from '~/components/header/HeaderMenuToggleButton.vue'
import HeaderSiteSearch from '~/components/header/HeaderSiteSearch.vue'
import TheUpperRightCornerBanner from '~/components/header/TheUpperRightCornerBanner.vue'
import {
  GET_LOCAL_HEADER,
  SET_HEADER_VISIBILITY,
} from '~/graphql/gqlLocal/localHeader'
import {
  GQLLocalHeader,
  GQLLocalOpenHeaderEnum,
  GQLLocalUi,
} from '~/graphql/__generated__/interfacesLocal'
import ROUTES from '~/config/routes'
import { GQL_LOCAL_UI } from '~/graphql/gqlLocal/localUi'
import { GQL_LOCAL_ACCOUNT } from '~/graphql/gqlLocal/localAccount'
import showWinterEdition from '~/utils/showWinterEdition'
import { isReviewApp } from '~/utils/testingBar'
import { GtmEventTypes } from '~/utils/tracking/gtm'
import { Shops } from '~/config/shops'

// TODO replace image with banner from banMan
@Component({
  setup() {
    const headerElRef = ref<HTMLElement | null>(null)

    const { isHidden, showHeader } = useHeaderScroll({
      headerEl: headerElRef,
      aboveHeaderElId: 'above-header',
    })

    return {
      headerElRef,
      isHiddenMobile: isHidden,
      showHeader,
    }
  },
  apollo: {
    account: {
      query: GQL_LOCAL_ACCOUNT,
    },
    header: {
      query: GET_LOCAL_HEADER,
    },
    ui: {
      prefetch: false,
      query: GQL_LOCAL_UI,
    },
  },
  components: {
    BasicImage,
    HeaderShell,
    HeaderDesktopIcons,
    HeaderHorizonMenu,
    HeaderMainMenu,
    HeaderMenuToggleButton,
    HeaderMobileIcons,
    HeaderSiteSearch,
    HeaderDealsButton,
    HeaderLogo,
    RoundedButton,
    TheUpperRightCornerBanner,
  },
})
export default class Header extends Vue {
  $webComponentsConfiguration?: WebComponentsConfiguration
  ui: GQLLocalUi
  header: GQLLocalHeader
  isHeaderVisible = true
  isCatalogOpen = false
  observer: IntersectionObserver
  @Prop({ default: '' }) cacheKeyPrefix: boolean

  @Ref('headerEl') readonly headerEl!: any

  // provided by the `setup()` component option above (Vue 2.7 Composition API)
  headerElRef!: HTMLElement | null
  isHiddenMobile!: boolean
  showHeader!: () => void

  @Watch('ui.openHeaderTab')
  showMobileHeader() {
    if (this.ui.openHeaderTab === GQLLocalOpenHeaderEnum.Cart) {
      this.showHeader()
    }
  }

  @Watch('isBonusSetGuard', { immediate: true })
  bonusSetCheck(value: boolean) {
    if (process.client && value) {
      this.isHeaderVisible = true
    }
  }

  @Watch('isHeaderVisible', { immediate: true })
  @Emit()
  onVisibilityChange(value: boolean) {
    return value
  }

  get cacheKey() {
    return `${this.cacheKeyPrefix}:${this.$shopConfig.shopId}`
  }

  get backgroundColor() {
    if (isReviewApp()) {
      return {
        backgroundColor: '#c9e2f3',
      }
    }
  }

  get extraButton() {
    return false // disabled due to GF-8720 (cannot use menu redesign AB test in KC)
    // return (
    //   this.$abTesting.getABtestVariantByName(
    //     AB_TESTS_NAME.MOBILE_MENU,
    //     AB_TEST_VARIANT.C
    //   ) && this.isHomepage
    // )
  }

  mounted() {
    this.headerElRef = this.headerEl?.$el as HTMLElement
    this.observer = new IntersectionObserver((entries) => {
      if (this.isBonusSet) {
        return
      }

      const [header] = entries
      this.isHeaderVisible = header.isIntersecting
    })

    this.observer.observe(this.$el)
  }

  openMenu(isMenuVisible: boolean) {
    this.gaEventClick(isMenuVisible ? 'Open' : 'Close')
    this.isCatalogOpen = isMenuVisible

    this.$apollo.mutate({
      mutation: SET_HEADER_VISIBILITY,
      variables: {
        isMenuVisible,
      },
    })
  }

  gaEventClick(eventLabel: string) {
    this.$pushAnalyticsEvent(GtmEventTypes.EventFire, {
      eventAction: 'Mobile navigation',
      eventCategory: 'Navigation',
      eventLabel,
      eventNonInteractionFlag: true,
      eventValue: 1,
    })
  }

  destroy() {
    this.observer.unobserve(this.$el)
  }

  get isHomepage() {
    return this.$route.name === ROUTES.homepage.name
  }

  get isDetail() {
    return this.$route.name === ROUTES.detail.name
  }

  get isBonusSet() {
    return this.$route.name === ROUTES.bonusSet.name
  }

  get isBonusSetGuard() {
    if (!process.client) {
      return false
    }
    return this.$route.name === ROUTES.bonusSet.name
  }

  get isWinterEdition() {
    return showWinterEdition(this.$shopConfig.webConfig)
  }

  // micro HeaderLogo renders a plain link -> client-side navigation (as BasicLink did),
  // standalone header (router disabled) & ctrl/cmd click keep the plain link behaviour
  goToHomepage(event: MouseEvent) {
    if (
      !this.$webComponentsConfiguration?.isNuxtRouterEnabled ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.button !== 0
    ) {
      return
    }

    event.preventDefault()
    this.$router.push({ path: '/' })
  }

  get logoByAllegro() {
    return MALL_ICONS.BY_ALLEGRO
  }

  get isSlovenia() {
    return this.$shopConfig.shopId === Shops.SI10SI
  }

  get showDealZone() {
    return this.$shopConfig.webConfig?.showDealZone === '1'
  }

  get dealsPath() {
    const path = this.isSlovenia
      ? '/koda-za-popust-akcije'
      : '/promo-kodovi-popusti-akcija'
    const baseUrl = this.$shopConfig.shopUrl.replace(/\/+$/, '')

    return `${baseUrl}${path}`
  }
}
